(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  let opening = false;
  let panelOpen = false;
  let syncScheduled = false;
  let bindScheduled = false;
  let positionScheduled = false;
  let prepareRetryTimer = null;
  let prepareRetryCount = 0;
  const MAX_PREPARE_RETRIES = 40;

  /*
    Closed launcher only: invisible hit-target on the teal FAB.
    This stylesheet is DISABLED while the chat panel is open so Shopify's
    history / maximize / minimize chrome is never overridden.
  */
  const LAUNCHER_OVERLAY_CSS = `
    .activator-nudge:has(button[aria-expanded="false"]),
    .activator-nudge--left:has(button[aria-expanded="false"]),
    .activator-nudge--right:has(button[aria-expanded="false"]) {
      background: transparent !important;
      background-color: transparent !important;
      border: 0 !important;
      bottom: var(--jft-chat-fab-bottom, 84px) !important;
      box-shadow: none !important;
      color: transparent !important;
      filter: opacity(0) !important;
      height: var(--jft-chat-fab-size, 52px) !important;
      left: auto !important;
      margin: 0 !important;
      max-height: none !important;
      max-width: none !important;
      opacity: 0 !important;
      overflow: hidden !important;
      padding: 0 !important;
      pointer-events: auto !important;
      position: fixed !important;
      right: var(--jft-chat-fab-right, 20px) !important;
      top: auto !important;
      transform: none !important;
      width: var(--jft-chat-fab-size, 52px) !important;
      z-index: 2147483002 !important;
    }

    button.activator[aria-expanded="false"],
    button[part="activator"][aria-expanded="false"],
    button.chat-toggle[aria-expanded="false"] {
      background: transparent !important;
      background-color: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      color: transparent !important;
      cursor: pointer !important;
      filter: opacity(0) !important;
      font-size: 0 !important;
      height: var(--jft-chat-fab-size, 52px) !important;
      line-height: 0 !important;
      margin: 0 !important;
      max-height: none !important;
      max-width: none !important;
      opacity: 0 !important;
      overflow: hidden !important;
      padding: 0 !important;
      pointer-events: auto !important;
      width: var(--jft-chat-fab-size, 52px) !important;
    }

    .activator-nudge:has(button[aria-expanded="false"]) .activator__icon,
    .activator-nudge:has(button[aria-expanded="false"]) .activator__label,
    .activator-nudge:has(button[aria-expanded="false"]) .activator__indicator,
    .activator-nudge:has(button[aria-expanded="false"]) svg,
    .activator-nudge:has(button[aria-expanded="false"]) img,
    button.activator[aria-expanded="false"] > *,
    button[part="activator"][aria-expanded="false"] > * {
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;

  // Keep brand tweaks narrow — never touch header chrome icons.
  const BRAND_CSS = `
    [class*="greeting"],
    [class*="Greeting"],
    [class*="intro"],
    [class*="Intro"] {
      color: #000000 !important;
    }

    [class*="greeting"] *,
    [class*="Greeting"] *,
    [class*="intro"] * {
      color: #000000 !important;
    }
  `;

  function getChatHost() {
    return (
      root.document.querySelector('inbox-online-store-chat')
      || root.document.getElementById('ShopifyChat')
      || root.document.querySelector('shopify-chat')
      || root.document.getElementById('shopify-chat')
    );
  }

  function getFab() {
    return root.document.querySelector('[data-footer-chat-trigger]');
  }

  function getInboxShadowRoots(host) {
    const roots = [];
    if (!host) {
      return roots;
    }

    const visit = (node) => {
      if (!node?.shadowRoot) {
        return;
      }
      roots.push(node.shadowRoot);
      node.shadowRoot.querySelectorAll('*').forEach((child) => {
        if (child.shadowRoot) {
          visit(child);
        }
      });
    };

    visit(host);
    return roots;
  }

  function queryFirstInShadows(host, selectors) {
    const list = Array.isArray(selectors) ? selectors : [selectors];
    for (const shadowRoot of getInboxShadowRoots(host)) {
      for (const selector of list) {
        try {
          const match = shadowRoot.querySelector(selector);
          if (match) {
            return match;
          }
        } catch (_error) {
          // ignore
        }
      }
    }
    return null;
  }

  function isVisibleElement(element) {
    if (!element) {
      return false;
    }
    try {
      const style = root.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
      }
    } catch (_error) {
      // ignore
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 24 && rect.height > 24;
  }

  function isChatOpen(host) {
    if (!host) {
      return false;
    }

    if (host.getAttribute('is-open') === 'true') {
      const panel = queryFirstInShadows(host, ['[role="dialog"]', 'textarea', 'input[placeholder]']);
      return !panel || isVisibleElement(panel);
    }

    const panel = queryFirstInShadows(host, [
      '[role="dialog"]',
      'textarea',
      'input[placeholder*="Ask" i]',
      'input[placeholder*="anything" i]',
      'input[placeholder*="message" i]',
      '[contenteditable="true"]',
    ]);

    if (!panel || !isVisibleElement(panel)) {
      return false;
    }

    return Boolean(
      queryFirstInShadows(host, [
        'button.activator[aria-expanded="true"]',
        'button[part="activator"][aria-expanded="true"]',
        'button.chat-toggle[aria-expanded="true"]',
      ])
    );
  }

  function setLauncherStylesEnabled(host, enabled) {
    getInboxShadowRoots(host).forEach((shadowRoot) => {
      const launcher = shadowRoot.querySelector('style[data-jft-inbox-launcher]');
      if (launcher) {
        // enabled=true means hide closed gray pill / overlay FAB hit-target
        // enabled=false means chat is open — let Shopify chrome render normally
        launcher.disabled = !enabled;
      }
    });
  }

  function setPanelOpenState(host, open) {
    const next = Boolean(open);
    if (panelOpen === next) {
      if (host) {
        host.classList.toggle('jft-chat-panel-open', next);
        setLauncherStylesEnabled(host, !next);
      }
      return;
    }

    panelOpen = next;
    root.document.body.classList.toggle('jft-inbox-open', next);
    root.document.body.classList.remove('jft-inbox-opening');

    if (host) {
      host.classList.toggle('jft-chat-panel-open', next);
      host.classList.add('jft-inbox-host');
      setLauncherStylesEnabled(host, !next);
      if (!next) {
        host.removeAttribute('data-jft-opening');
      }
    }

    syncLauncherPosition();
  }

  function syncInboxOpenState(host) {
    const nextHost = host || getChatHost();
    if (!nextHost) {
      setPanelOpenState(null, false);
      return;
    }
    setPanelOpenState(nextHost, isChatOpen(nextHost));
  }

  function scheduleSync(host) {
    if (syncScheduled) {
      return;
    }
    syncScheduled = true;
    root.requestAnimationFrame(() => {
      syncScheduled = false;
      syncInboxOpenState(host || getChatHost());
    });
  }

  function ensureStyle(shadowRoot, attr, cssText) {
    if (!shadowRoot) {
      return null;
    }
    let style = shadowRoot.querySelector(`style[${attr}]`);
    if (!style) {
      style = root.document.createElement('style');
      style.setAttribute(attr, 'true');
      shadowRoot.appendChild(style);
    }
    if (style.textContent !== cssText) {
      style.textContent = cssText;
    }
    return style;
  }

  function syncLauncherPosition() {
    const fab = getFab();
    const host = getChatHost();
    if (!fab || !host || panelOpen) {
      return;
    }

    const rect = fab.getBoundingClientRect();
    if (rect.width < 8 || rect.height < 8) {
      return;
    }

    const bottom = Math.max(0, Math.round(root.innerHeight - rect.bottom));
    const right = Math.max(0, Math.round(root.innerWidth - rect.right));
    const size = Math.round(Math.max(rect.width, rect.height));

    host.style.setProperty('--jft-chat-fab-bottom', `${bottom}px`);
    host.style.setProperty('--jft-chat-fab-right', `${right}px`);
    host.style.setProperty('--jft-chat-fab-size', `${size}px`);

    getInboxShadowRoots(host).forEach((shadowRoot) => {
      shadowRoot.host?.style?.setProperty?.('--jft-chat-fab-bottom', `${bottom}px`);
      // Custom properties inherit into shadow from host element.
    });
  }

  function schedulePositionSync() {
    if (positionScheduled) {
      return;
    }
    positionScheduled = true;
    root.requestAnimationFrame(() => {
      positionScheduled = false;
      syncLauncherPosition();
    });
  }

  function observeInboxOpenState(host) {
    if (!host || host.dataset.jftOpenObserved === 'true') {
      return;
    }

    host.dataset.jftOpenObserved = 'true';
    syncInboxOpenState(host);

    new MutationObserver(() => {
      scheduleSync(host);
    }).observe(host, {
      attributes: true,
      attributeFilter: ['is-open', 'view'],
    });
  }

  function bindCloseDetection(host, shadowRoot) {
    if (!shadowRoot || shadowRoot.jftCloseBound) {
      return;
    }

    shadowRoot.jftCloseBound = true;
    shadowRoot.addEventListener(
      'click',
      (event) => {
        const el = event.target && event.target.closest
          ? event.target.closest('button, a, [role="button"]')
          : null;
        if (!el) {
          return;
        }

        const label = `${el.getAttribute('aria-label') || ''} ${el.textContent || ''}`
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        const isActivator = el.matches
          && el.matches('button.activator, button[part="activator"], button.chat-toggle');
        const looksLikeClose =
          /close|minimize|collapse|dismiss|đóng/.test(label)
          || String(el.className || '').toLowerCase().includes('close')
          || (isActivator && el.getAttribute('aria-expanded') === 'true');

        if (!looksLikeClose) {
          return;
        }

        let ticks = 0;
        const timer = root.setInterval(() => {
          ticks += 1;
          const open = isChatOpen(host);
          setPanelOpenState(host, open);
          if (!open || ticks >= 40) {
            root.clearInterval(timer);
            if (!open) {
              schedulePositionSync();
            }
          }
        }, 40);
      },
      true
    );
  }

  function observeShadowOpenState(host, shadowRoot) {
    if (!shadowRoot) {
      return;
    }

    if (!shadowRoot.jftObserved) {
      shadowRoot.jftObserved = true;
      new MutationObserver(() => {
        scheduleBindAndHide();
        scheduleSync(host);
      }).observe(shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['aria-expanded', 'open', 'hidden'],
      });
    }

    bindCloseDetection(host, shadowRoot);
  }

  function prepareChatHost(host) {
    if (!host) {
      return false;
    }

    host.classList.add('jft-inbox-host');
    observeInboxOpenState(host);

    const shadowRoots = getInboxShadowRoots(host);
    shadowRoots.forEach((shadowRoot) => {
      ensureStyle(shadowRoot, 'data-jft-inbox-brand', BRAND_CSS);
      ensureStyle(shadowRoot, 'data-jft-inbox-launcher', LAUNCHER_OVERLAY_CSS);
      observeShadowOpenState(host, shadowRoot);
    });

    setLauncherStylesEnabled(host, !isChatOpen(host));
    host.dataset.jftToggleHidden = 'true';
    schedulePositionSync();
    scheduleSync(host);
    return shadowRoots.length > 0;
  }

  function findOpenButton(host) {
    return (
      queryFirstInShadows(host, [
        'button.activator[aria-expanded="false"]',
        'button[part="activator"][aria-expanded="false"]',
        'button.chat-toggle:not([aria-expanded="true"])',
        'button.activator',
        'button[part="activator"]',
        'button.chat-toggle',
        'button[aria-label="Chat"]',
      ])
    );
  }

  function triggerClick(element) {
    if (!element) {
      return false;
    }
    element.dispatchEvent(
      new MouseEvent('pointerdown', { bubbles: true, cancelable: true, composed: true, view: root })
    );
    element.dispatchEvent(
      new MouseEvent('mousedown', { bubbles: true, cancelable: true, composed: true, view: root })
    );
    element.dispatchEvent(
      new MouseEvent('mouseup', { bubbles: true, cancelable: true, composed: true, view: root })
    );
    element.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: root })
    );
    element.click();
    return true;
  }

  function delay(ms) {
    return new Promise((resolve) => root.setTimeout(resolve, ms));
  }

  async function waitUntil(predicate, timeoutMs = 1200, stepMs = 40) {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (predicate()) {
        return true;
      }
      await delay(stepMs);
    }
    return predicate();
  }

  function isLocalThemePreview() {
    const hostname = root.location.hostname;
    const port = String(root.location.port || '');
    return hostname === '127.0.0.1' || hostname === 'localhost' || port === '9292';
  }

  function buildStoreChatPreviewUrl(trigger) {
    const origin =
      (trigger && trigger.getAttribute('data-shop-origin'))
      || (root.Shopify && root.Shopify.shop ? `https://${root.Shopify.shop}` : '');
    if (!origin) {
      return null;
    }
    try {
      const url = new URL(origin);
      const themeId =
        (trigger && trigger.getAttribute('data-theme-id'))
        || (root.Shopify && root.Shopify.theme && root.Shopify.theme.id);
      if (themeId) {
        url.searchParams.set('preview_theme_id', String(themeId));
      }
      url.searchParams.set('show_chat', '1');
      return url.toString();
    } catch (_error) {
      return null;
    }
  }

  // Fallback only — primary open path is the invisible native activator over the FAB.
  async function openStoreChat(fallbackUrl, trigger) {
    if (opening) {
      return false;
    }

    if (isLocalThemePreview()) {
      const previewUrl = buildStoreChatPreviewUrl(trigger);
      if (previewUrl) {
        root.location.href = previewUrl;
        return true;
      }
      if (fallbackUrl) {
        root.location.href = fallbackUrl;
      }
      return false;
    }

    opening = true;
    try {
      const host = getChatHost();
      if (!host) {
        // Chat script still loading — retry briefly instead of forcing show_chat.
        const ready = await waitUntil(() => Boolean(getChatHost()?.shadowRoot), 4000, 100);
        if (!ready) {
          return false;
        }
      }

      const chatHost = getChatHost();
      prepareChatHost(chatHost);
      syncLauncherPosition();

      if (isChatOpen(chatHost)) {
        setPanelOpenState(chatHost, true);
        return true;
      }

      setPanelOpenState(chatHost, false);
      const target = findOpenButton(chatHost);
      if (!target) {
        return false;
      }

      triggerClick(target);
      const opened = await waitUntil(() => isChatOpen(chatHost), 2000);
      setPanelOpenState(chatHost, opened);
      return opened;
    } finally {
      opening = false;
      scheduleSync(getChatHost());
      schedulePositionSync();
    }
  }

  function bindChatTrigger(element) {
    if (!element || element.dataset.jftChatBound === 'true') {
      return;
    }

    element.dataset.jftChatBound = 'true';
    // Fallback when native overlay activator is not ready yet.
    element.addEventListener(
      'click',
      (event) => {
        // Fallback when Inbox script is still loading and overlay activator is not ready.
        event.preventDefault();
        event.stopPropagation();
        openStoreChat(element.getAttribute('data-contact-url') || '/pages/contact', element);
      },
      true
    );
  }

  function bindFooterChatButtons() {
    root.document.querySelectorAll('[data-footer-chat-trigger]').forEach(bindChatTrigger);
  }

  function schedulePrepareRetry() {
    if (prepareRetryTimer || prepareRetryCount >= MAX_PREPARE_RETRIES) {
      return;
    }

    prepareRetryCount += 1;
    const retryDelay = Math.min(1000, 100 + (prepareRetryCount * 50));
    prepareRetryTimer = root.setTimeout(() => {
      prepareRetryTimer = null;
      scheduleBindAndHide();
    }, retryDelay);
  }

  function scheduleBindAndHide() {
    if (bindScheduled) {
      return;
    }
    bindScheduled = true;
    root.setTimeout(() => {
      bindScheduled = false;
      const host = getChatHost();
      if (host) {
        const prepared = prepareChatHost(host);
        if (prepared) {
          prepareRetryCount = 0;
          if (prepareRetryTimer) {
            root.clearTimeout(prepareRetryTimer);
            prepareRetryTimer = null;
          }
        } else {
          schedulePrepareRetry();
        }
      }
      bindFooterChatButtons();
      scheduleSync(host);
      schedulePositionSync();
    }, 100);
  }

  function openFromShowChatParam() {
    try {
      const params = new URLSearchParams(root.location.search);
      if (!params.has('show_chat')) {
        return;
      }
      // Shopify opens natively with this param; we only sync FAB visibility.
      root.setTimeout(() => {
        scheduleBindAndHide();
        let ticks = 0;
        const timer = root.setInterval(() => {
          ticks += 1;
          syncInboxOpenState(getChatHost());
          if (panelOpen || ticks >= 50) {
            root.clearInterval(timer);
          }
        }, 100);
      }, 300);
    } catch (_error) {
      // ignore
    }
  }

  function watchInboxHost() {
    if (!root.document) {
      return;
    }

    scheduleBindAndHide();
    openFromShowChatParam();

    new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        if (mutation.type !== 'childList') {
          return false;
        }
        return [...mutation.addedNodes].some((node) => {
          if (!node || node.nodeType !== 1) {
            return false;
          }
          const tag = node.tagName || '';
          return (
            tag === 'SHOPIFY-CHAT'
            || tag === 'INBOX-ONLINE-STORE-CHAT'
            || (typeof node.matches === 'function'
              && node.matches('shopify-chat, inbox-online-store-chat, [data-footer-chat-trigger]'))
            || (typeof node.querySelector === 'function'
              && node.querySelector('shopify-chat, inbox-online-store-chat, [data-footer-chat-trigger]'))
          );
        });
      });
      if (relevant) {
        scheduleBindAndHide();
      }
    }).observe(root.document.documentElement, {
      childList: true,
      subtree: true,
    });

    root.addEventListener('resize', schedulePositionSync, { passive: true });
    root.addEventListener('scroll', schedulePositionSync, { passive: true });
  }

  root.openStoreChat = openStoreChat;

  if (root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', watchInboxHost, { once: true });
    } else {
      watchInboxHost();
    }
  }
})();
